import { useAuth0 } from "@auth0/auth0-react";
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  CreateCareResourceWeeklyAllocationBlockMutationVariables,
  DayScheduleType,
  useCareAppointmentsByResourceEmailQuery,
  useCareResourceByEmailQuery,
  useCareResourceSingleAllocationBlocksQuery,
  useCareResourceWeeklyAllocationBlocksQuery,
  useCreateCareResourceSingleAllocationBlockMutation,
  useCreateCareResourceWeeklyAllocationBlockMutation,
  useDeleteCareResourceSingleAllocationBlockMutation,
  useDeleteCareResourceWeeklyAllocationBlockMutation,
} from "@api/graphql/types-and-hooks";
import { OnSchedResourcePill } from "@components/OnSchedResourcePill";
import { WarningCard } from "@components/WarningCard/WarningCard";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Helper function to get the Monday of a given date's week
const getMondayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Helper function to format date as YYYY-MM-DD in local timezone
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to format datetime for OnSched API (YYYY-MM-DDTHH:MM:SS.mmm)
const formatDateTimeForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

// Helper to format time in 12-hour format
const formatTime12Hour = (hour: number): string => {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

// Time constants (6am to 8pm = 14 hours)
const START_HOUR = 6;
const END_HOUR = 20;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const HOUR_HEIGHT = 72; // pixels per hour

// Colors
const WEEKLY_ALLOCATION_COLOR = "bg-teal-200 border-teal-400";
const SINGLE_ALLOCATION_COLOR = "bg-teal-500 border-teal-700";
const SCHEDULED_APPOINTMENT_COLOR = "bg-orange-500 border-orange-700";
const APPOINTMENT_PADDING_COLOR = "bg-orange-300 border-orange-500 opacity-60";

export const MyAvailabilityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const userEmail = user?.email;

  // Track current week (starting Monday)
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getMondayOfWeek(new Date()));

  // Track hovered weekly allocation ID for highlighting
  const [hoveredWeeklyAllocationId, setHoveredWeeklyAllocationId] = useState<string | null>(null);

  // Form state for creating new single allocation
  const [singleStartDateTime, setSingleStartDateTime] = useState<string>("");
  const [singleEndDateTime, setSingleEndDateTime] = useState<string>("");
  const [singleFormError, setSingleFormError] = useState<string>("");

  // Form state for creating weekly allocation
  const [weeklyStartDate, setWeeklyStartDate] = useState<string>("");
  const [weeklyEndDate, setWeeklyEndDate] = useState<string>("");
  const [weeklySchedule, setWeeklySchedule] = useState<{
    sunday?: { start: string; end: string };
    monday?: { start: string; end: string };
    tuesday?: { start: string; end: string };
    wednesday?: { start: string; end: string };
    thursday?: { start: string; end: string };
    friday?: { start: string; end: string };
    saturday?: { start: string; end: string };
  }>({});
  const [weeklyFormError, setWeeklyFormError] = useState<string>("");

  // Query: Check if provider exists in OnSched
  const { data: resourceData, isLoading: isResourceLoading } = useCareResourceByEmailQuery(
    { email: userEmail! },
    {
      enabled: !!userEmail,
      retry: false,
    },
  );

  const onschedResource = resourceData?.careResourceByEmail;
  const resourceExists = !!onschedResource;

  // Calculate week end (6 days after start = Sunday)
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);

  // Query: Get single allocations for the week
  const {
    data: singleAllocationsData,
    isLoading: isSingleAllocationsLoading,
    refetch: refetchSingleAllocations,
  } = useCareResourceSingleAllocationBlocksQuery(
    {
      resourceEmail: userEmail!,
      startDate: formatDate(currentWeekStart),
      endDate: formatDate(currentWeekEnd),
    },
    {
      enabled: !!userEmail && resourceExists,
      retry: false,
    },
  );

  // Query: Get weekly allocations for the week
  const {
    data: weeklyAllocationsData,
    isLoading: isWeeklyAllocationsLoading,
    refetch: refetchWeeklyAllocations,
  } = useCareResourceWeeklyAllocationBlocksQuery(
    {
      resourceEmail: userEmail!,
      startDate: formatDate(currentWeekStart),
      endDate: formatDate(currentWeekEnd),
    },
    {
      enabled: !!userEmail && resourceExists,
      retry: false,
    },
  );

  // Query: Get scheduled appointments for the week
  const { data: appointmentsData, isLoading: isAppointmentsLoading } = useCareAppointmentsByResourceEmailQuery(
    {
      email: userEmail!,
      startDate: formatDate(currentWeekStart),
      endDate: formatDate(currentWeekEnd),
    },
    {
      enabled: !!userEmail && resourceExists,
      retry: false,
    },
  );

  const singleAllocations = singleAllocationsData?.careResourceSingleAllocationBlocks || [];
  const weeklyAllocations = weeklyAllocationsData?.careResourceWeeklyAllocationBlocks || [];
  const appointments = appointmentsData?.careAppointmentsByResourceEmail || [];

  // Helper to get padding minutes for a service
  const getPaddingForService = (serviceId: string): number => {
    const service = onschedResource?.services?.find((s) => s?.id === serviceId);
    return service?.paddingMinutes ?? 0;
  };

  // Mutations
  const createSingleAllocationMutation = useCreateCareResourceSingleAllocationBlockMutation();
  const deleteSingleAllocationMutation = useDeleteCareResourceSingleAllocationBlockMutation();
  const createWeeklyAllocationMutation = useCreateCareResourceWeeklyAllocationBlockMutation();
  const deleteWeeklyAllocationMutation = useDeleteCareResourceWeeklyAllocationBlockMutation();

  // Navigation handlers
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMondayOfWeek(new Date()));
  };

  const isCurrentWeek = formatDate(getMondayOfWeek(new Date())) === formatDate(currentWeekStart);

  // Handle delete single allocation
  const handleDeleteSingleAllocation = async (allocationId: string) => {
    try {
      await deleteSingleAllocationMutation.mutateAsync({ allocationId });
      notifySuccess("Single availability deleted successfully!");
      refetchSingleAllocations();
    } catch (error) {
      notifyError((error as Error).message || "Failed to delete single availability");
    }
  };

  // Handle delete weekly allocation
  const handleDeleteWeeklyAllocation = async (allocationId: string) => {
    try {
      await deleteWeeklyAllocationMutation.mutateAsync({ allocationId });
      notifySuccess("Weekly availability deleted successfully!");
      refetchWeeklyAllocations();
    } catch (error) {
      notifyError((error as Error).message || "Failed to delete weekly availability");
    }
  };

  // Handle create single allocation
  const handleCreateSingleAllocation = async () => {
    setSingleFormError("");

    if (!singleStartDateTime || !singleEndDateTime) {
      setSingleFormError("Please select both start and end times");
      return;
    }

    const start = new Date(singleStartDateTime);
    const end = new Date(singleEndDateTime);

    if (end <= start) {
      setSingleFormError("End time must be after start time");
      return;
    }

    try {
      await createSingleAllocationMutation.mutateAsync({
        resourceEmail: userEmail!,
        startDatetime: formatDateTimeForAPI(start),
        endDatetime: formatDateTimeForAPI(end),
      });

      notifySuccess("Single availability created successfully!");
      setSingleStartDateTime("");
      setSingleEndDateTime("");
      refetchSingleAllocations();
    } catch (error) {
      notifyError((error as Error).message || "Failed to create single availability");
    }
  };

  // Handle create weekly allocation
  const handleCreateWeeklyAllocation = async () => {
    setWeeklyFormError("");

    if (!weeklyStartDate || !weeklyEndDate) {
      setWeeklyFormError("Please select both start and end dates");
      return;
    }

    // Check if at least one day is selected
    const hasAnyDay = Object.keys(weeklySchedule).some((key) => weeklySchedule[key as keyof typeof weeklySchedule]);
    if (!hasAnyDay) {
      setWeeklyFormError("Please select at least one day with time ranges");
      return;
    }

    try {
      const payload: CreateCareResourceWeeklyAllocationBlockMutationVariables = {
        resourceEmail: userEmail!,
        startDate: weeklyStartDate,
        endDate: weeklyEndDate,
        ...(weeklySchedule.sunday && {
          sunday: {
            startTime: weeklySchedule.sunday.start,
            endTime: weeklySchedule.sunday.end,
          },
        }),
        ...(weeklySchedule.monday && {
          monday: {
            startTime: weeklySchedule.monday.start,
            endTime: weeklySchedule.monday.end,
          },
        }),
        ...(weeklySchedule.tuesday && {
          tuesday: {
            startTime: weeklySchedule.tuesday.start,
            endTime: weeklySchedule.tuesday.end,
          },
        }),
        ...(weeklySchedule.wednesday && {
          wednesday: {
            startTime: weeklySchedule.wednesday.start,
            endTime: weeklySchedule.wednesday.end,
          },
        }),
        ...(weeklySchedule.thursday && {
          thursday: {
            startTime: weeklySchedule.thursday.start,
            endTime: weeklySchedule.thursday.end,
          },
        }),
        ...(weeklySchedule.friday && {
          friday: {
            startTime: weeklySchedule.friday.start,
            endTime: weeklySchedule.friday.end,
          },
        }),
        ...(weeklySchedule.saturday && {
          saturday: {
            startTime: weeklySchedule.saturday.start,
            endTime: weeklySchedule.saturday.end,
          },
        }),
      };

      await createWeeklyAllocationMutation.mutateAsync(payload);

      notifySuccess("Weekly availability created successfully!");
      setWeeklyStartDate("");
      setWeeklyEndDate("");
      setWeeklySchedule({});
      refetchWeeklyAllocations();
    } catch (error) {
      notifyError((error as Error).message || "Failed to create weekly availability");
    }
  };

  // Get day name from index (0 = Sunday)
  const getDayName = (dayIndex: number): string => {
    return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayIndex];
  };

  // Get day key for weekly schedule
  const getDayKey = (dayIndex: number): string => {
    return ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][dayIndex];
  };

  // Render allocation block
  const renderAllocationBlock = (
    startTime: string,
    endTime: string,
    id: string,
    type: "single" | "weekly" | "appointment" | "padding",
    onDelete?: () => void,
    weeklyAllocationId?: string,
    appointmentCustomer?: string,
    serviceName?: string,
    paddingMinutes?: number,
    isPast?: boolean,
  ) => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();

    // Calculate position and height relative to 6am-8pm range
    const dayStartMinutes = START_HOUR * 60;

    const topPosition = ((startMinutes - dayStartMinutes) / 60) * HOUR_HEIGHT;
    const height = ((endMinutes - startMinutes) / 60) * HOUR_HEIGHT;

    const colorClass =
      type === "padding"
        ? APPOINTMENT_PADDING_COLOR
        : type === "appointment"
          ? SCHEDULED_APPOINTMENT_COLOR
          : type === "weekly"
            ? WEEKLY_ALLOCATION_COLOR
            : SINGLE_ALLOCATION_COLOR;

    // Highlight if this is part of the hovered weekly allocation
    const isHighlighted = type === "weekly" && weeklyAllocationId && hoveredWeeklyAllocationId === weeklyAllocationId;

    // Apply opacity for past appointments
    const opacityClass = isPast && (type === "appointment" || type === "padding") ? "opacity-40" : "";

    const label =
      type === "appointment"
        ? `${appointmentCustomer}`
        : type === "weekly"
          ? "Weekly Allocation Block"
          : "Single Allocation Block";

    return (
      <div
        key={id}
        className={`absolute left-2 right-2 ${colorClass} ${opacityClass} border rounded-sm p-1 text-xs shadow-xs group hover:shadow-md transition-all ${isHighlighted ? "ring-2 ring-teal-600 shadow-lg" : ""}`}
        style={{
          top: `${topPosition}px`,
          height: `${height}px`,
        }}
        onMouseEnter={() => {
          if (type === "weekly" && weeklyAllocationId) {
            setHoveredWeeklyAllocationId(weeklyAllocationId);
          }
        }}
        onMouseLeave={() => {
          if (type === "weekly") {
            setHoveredWeeklyAllocationId(null);
          }
        }}
      >
        {type !== "appointment" && type !== "padding" && onDelete && (
          <button
            onClick={onDelete}
            className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete"
          >
            <XMarkIcon className="w-2.5 h-2.5 text-white" />
          </button>
        )}
        {type === "padding" ? (
          <div className="flex items-center justify-center h-full">
            <div className="font-medium text-sm text-orange-700">{paddingMinutes} min</div>
          </div>
        ) : (
          <>
            <div className={`font-medium text-sm ${type === "appointment" ? "text-white" : "text-black"}`}>
              {start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })} -{" "}
              {end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
            </div>
            <div className={`text-xs ${type === "appointment" ? "text-white" : "text-gray-900"}`}>{label}</div>
            {type === "appointment" && serviceName && (
              <div className="text-xs text-white opacity-90 mt-0.5">{serviceName}</div>
            )}
          </>
        )}
      </div>
    );
  };

  // Loading state
  if (isResourceLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-row gap-2 items-center cursor-pointer" onClick={() => navigate("/frontera-clarity")}>
          <ChevronLeftIcon className="text-green-700 w-5 h-5" />
          <Paragraph size="small">Back</Paragraph>
        </div>
        <div className="p-12 text-center bg-white border border-gray-200 rounded-lg">
          <Paragraph>Loading...</Paragraph>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-row gap-2 items-center cursor-pointer" onClick={() => navigate("/frontera-clarity")}>
        <ChevronLeftIcon className="text-green-700 w-5 h-5" />
        <Paragraph size="small">Back</Paragraph>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-start mb-6">
          <Headline size="medium" className="text-2xl font-semibold text-gray-900">
            My Availability
          </Headline>

          {resourceExists && <OnSchedResourcePill />}
        </div>

        {!resourceExists && (
          <WarningCard content="You are not set up as a provider in the scheduling system. Please contact support to get access.">
            <Small className="text-gray-600 mt-2">Email: {userEmail}</Small>
          </WarningCard>
        )}

        {resourceExists && (
          <div className="space-y-6">
            {/* Week Navigation */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <button
                onClick={goToPreviousWeek}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous Week
              </button>

              <div className="flex items-center gap-4">
                {!isCurrentWeek && (
                  <button
                    onClick={goToCurrentWeek}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition"
                  >
                    Today
                  </button>
                )}
                <Paragraph className="font-medium">
                  {`${currentWeekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${currentWeekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                </Paragraph>
              </div>

              <button
                onClick={goToNextWeek}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition"
              >
                Next Week
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Calendar View */}
            {(isSingleAllocationsLoading || isWeeklyAllocationsLoading || isAppointmentsLoading) && (
              <div className="p-8 text-center">
                <Paragraph>Loading availability...</Paragraph>
              </div>
            )}

            {!isSingleAllocationsLoading && !isWeeklyAllocationsLoading && !isAppointmentsLoading && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
                  {/* Time column header */}
                  <div className="border-r border-gray-200 p-2">
                    <Small className="text-gray-600 font-medium">Time</Small>
                  </div>

                  {/* Day headers */}
                  {Array.from({ length: 7 }, (_, dayIndex) => {
                    const dayDate = new Date(currentWeekStart);
                    dayDate.setDate(dayDate.getDate() + dayIndex);
                    const isToday = formatDate(dayDate) === formatDate(new Date());

                    return (
                      <div
                        key={dayIndex}
                        className={`border-r border-gray-200 last:border-r-0 p-2 text-center ${isToday ? "bg-teal-100" : ""}`}
                      >
                        <Paragraph className={`font-semibold ${isToday ? "text-teal-700" : "text-gray-900"}`}>
                          {getDayName(dayDate.getDay()).slice(0, 3)}
                        </Paragraph>
                        <Small className={isToday ? "text-teal-600" : "text-gray-600"}>
                          {dayDate.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                        </Small>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-8">
                  {/* Time column */}
                  <div className="border-r border-gray-200 bg-gray-50">
                    {Array.from({ length: TOTAL_HOURS }, (_, i) => {
                      const hour = START_HOUR + i;
                      return (
                        <div key={hour} className="h-[72px] border-b border-gray-200 p-1 text-right">
                          <Small className="text-gray-600 text-xs">{formatTime12Hour(hour)}</Small>
                        </div>
                      );
                    })}
                  </div>

                  {/* Day columns */}
                  {Array.from({ length: 7 }, (_, dayIndex) => {
                    const dayDate = new Date(currentWeekStart);
                    dayDate.setDate(dayDate.getDate() + dayIndex);
                    const dateStr = formatDate(dayDate);
                    const dayKey = getDayKey(dayDate.getDay());

                    // Filter single allocations for this day
                    const daySingleAllocations = singleAllocations.filter((alloc) => {
                      if (!alloc || alloc.deleted) return false;
                      const allocDate = formatDate(new Date(alloc.startTime));
                      return allocDate === dateStr;
                    });

                    // Filter scheduled appointments for this day
                    const dayAppointments = appointments.filter((appt) => {
                      if (!appt || appt.status === "cancelled") return false;
                      const apptDate = formatDate(new Date(appt.startTime));
                      return apptDate === dateStr;
                    });

                    // Filter weekly allocations that apply to this day
                    const dayWeeklyBlocks: Array<{
                      id: string;
                      startTime: string;
                      endTime: string;
                    }> = [];

                    weeklyAllocations.forEach((weeklyAlloc) => {
                      if (!weeklyAlloc || weeklyAlloc.deleted) return;

                      const allocStartDate = new Date(weeklyAlloc.startDate);
                      const allocEndDate = new Date(weeklyAlloc.endDate);
                      const currentDate = new Date(dateStr);

                      // Check if current date is within the weekly allocation range
                      if (currentDate >= allocStartDate && currentDate <= allocEndDate) {
                        const daySchedule = weeklyAlloc[dayKey as keyof typeof weeklyAlloc] as DayScheduleType;
                        if (daySchedule && daySchedule.startTime && daySchedule.endTime) {
                          // Create a time block for this day
                          dayWeeklyBlocks.push({
                            id: `${weeklyAlloc.id}-${dateStr}`,
                            startTime: `${dateStr}T${daySchedule.startTime}:00`,
                            endTime: `${dateStr}T${daySchedule.endTime}:00`,
                          });
                        }
                      }
                    });

                    return (
                      <div key={dayIndex} className="border-r border-gray-200 last:border-r-0 relative">
                        {/* Hour grid lines */}
                        {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                          <div key={i} className="h-[72px] border-b border-gray-200" />
                        ))}

                        {/* Allocations layer */}
                        <div className="absolute inset-0 pointer-events-none">
                          <div className="relative h-full pointer-events-auto">
                            {/* Weekly allocations (render first, will be behind single allocations) */}
                            {dayWeeklyBlocks.map((block) => {
                              // Find the parent weekly allocation to get its ID
                              const parentAlloc = weeklyAllocations.find((alloc) => block.id.startsWith(alloc!.id));
                              return renderAllocationBlock(
                                block.startTime,
                                block.endTime,
                                block.id,
                                "weekly",
                                () => {
                                  if (parentAlloc) {
                                    handleDeleteWeeklyAllocation(parentAlloc.id);
                                  }
                                },
                                parentAlloc?.id,
                              );
                            })}

                            {/* Single allocations */}
                            {daySingleAllocations.map(
                              (alloc) =>
                                alloc &&
                                renderAllocationBlock(alloc.startTime, alloc.endTime, alloc.id, "single", () =>
                                  handleDeleteSingleAllocation(alloc.id),
                                ),
                            )}

                            {/* Scheduled appointments with padding */}
                            {dayAppointments.map((appt) => {
                              if (!appt) return null;

                              const paddingMinutes = getPaddingForService(appt.serviceId);
                              const appointmentStart = new Date(appt.startTime);
                              const appointmentEnd = new Date(appt.endTime);

                              // Check if appointment is in the past (compare dates only, not times)
                              const now = new Date();
                              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                              const appointmentDate = new Date(
                                appointmentStart.getFullYear(),
                                appointmentStart.getMonth(),
                                appointmentStart.getDate(),
                              );
                              const isPast = appointmentDate < today;

                              const blocks = [];

                              // Render before-padding block if it exists
                              if (paddingMinutes > 0) {
                                const paddingStart = new Date(appointmentStart);
                                paddingStart.setMinutes(paddingStart.getMinutes() - paddingMinutes);
                                blocks.push(
                                  renderAllocationBlock(
                                    paddingStart.toISOString(),
                                    appt.startTime,
                                    `${appt.id}-before`,
                                    "padding",
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    paddingMinutes,
                                    isPast,
                                  ),
                                );
                              }

                              // Render appointment block
                              blocks.push(
                                renderAllocationBlock(
                                  appt.startTime,
                                  appt.endTime,
                                  appt.id,
                                  "appointment",
                                  undefined,
                                  undefined,
                                  `${appt.customerFirstName} ${appt.customerLastName}`,
                                  appt.serviceName ?? undefined,
                                  undefined,
                                  isPast,
                                ),
                              );

                              // Render after-padding block if it exists
                              if (paddingMinutes > 0) {
                                const paddingEnd = new Date(appointmentEnd);
                                paddingEnd.setMinutes(paddingEnd.getMinutes() + paddingMinutes);
                                blocks.push(
                                  renderAllocationBlock(
                                    appt.endTime,
                                    paddingEnd.toISOString(),
                                    `${appt.id}-after`,
                                    "padding",
                                    undefined,
                                    undefined,
                                    undefined,
                                    undefined,
                                    paddingMinutes,
                                    isPast,
                                  ),
                                );
                              }

                              return blocks;
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-6 justify-center flex-wrap">
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 ${WEEKLY_ALLOCATION_COLOR} border rounded-sm`}></div>
                <Small className="text-gray-600">Weekly Availability</Small>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 ${SINGLE_ALLOCATION_COLOR} border rounded-sm`}></div>
                <Small className="text-gray-600">Single Availability</Small>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 ${SCHEDULED_APPOINTMENT_COLOR} border rounded-sm`}></div>
                <Small className="text-gray-600">Scheduled Appointments</Small>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 ${APPOINTMENT_PADDING_COLOR} border rounded-sm`}></div>
                <Small className="text-gray-600">Buffer Time</Small>
              </div>
            </div>

            {/* Input Forms Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Single Allocation Form */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <PlusIcon className="w-5 h-5 text-teal-600" />
                  <Paragraph className="font-semibold text-gray-900">Single Slot Allocation</Paragraph>
                </div>
                <Small className="text-gray-600 mb-3 block">One-time availability block</Small>

                <div className="space-y-3">
                  <div>
                    <label htmlFor="single-start" className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date & Time
                    </label>
                    <input
                      id="single-start"
                      type="datetime-local"
                      value={singleStartDateTime}
                      onChange={(e) => setSingleStartDateTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="single-end" className="block text-sm font-medium text-gray-700 mb-1">
                      End Date & Time
                    </label>
                    <input
                      id="single-end"
                      type="datetime-local"
                      value={singleEndDateTime}
                      onChange={(e) => setSingleEndDateTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <Button
                    text={createSingleAllocationMutation.isPending ? "Creating..." : "Add Single Availability"}
                    onClick={handleCreateSingleAllocation}
                    disabled={createSingleAllocationMutation.isPending || !singleStartDateTime || !singleEndDateTime}
                    appearance="primary"
                    className="w-full"
                  />

                  {singleFormError && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                      <Small className="text-red-800">{singleFormError}</Small>
                    </div>
                  )}
                </div>
              </div>

              {/* Weekly Allocation Form */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-1">
                  <PlusIcon className="w-5 h-5 text-teal-600" />
                  <Paragraph className="font-semibold text-gray-900">Weekly Slots Allocation</Paragraph>
                </div>
                <Small className="text-gray-600 mb-3 block">
                  Set availability for specific days across a date range
                </Small>

                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="weekly-start-date" className="block text-xs font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        id="weekly-start-date"
                        type="date"
                        value={weeklyStartDate}
                        onChange={(e) => setWeeklyStartDate(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="weekly-end-date" className="block text-xs font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        id="weekly-end-date"
                        type="date"
                        value={weeklyEndDate}
                        onChange={(e) => setWeeklyEndDate(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  {["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map((day) => (
                    <div key={day} className="border-t border-gray-200 pt-2">
                      <label className="flex items-center gap-2 mb-1">
                        <input
                          type="checkbox"
                          checked={!!weeklySchedule[day as keyof typeof weeklySchedule]}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWeeklySchedule({
                                ...weeklySchedule,
                                [day]: { start: "09:00", end: "17:00" },
                              });
                            } else {
                              const newSchedule = { ...weeklySchedule };
                              delete newSchedule[day as keyof typeof weeklySchedule];
                              setWeeklySchedule(newSchedule);
                            }
                          }}
                          className="rounded-sm border-gray-300"
                        />
                        <span className="text-sm font-medium text-gray-700 capitalize">{day}</span>
                      </label>
                      {weeklySchedule[day as keyof typeof weeklySchedule] && (
                        <div className="grid grid-cols-2 gap-2 ml-6">
                          <input
                            type="time"
                            value={weeklySchedule[day as keyof typeof weeklySchedule]!.start}
                            onChange={(e) =>
                              setWeeklySchedule({
                                ...weeklySchedule,
                                [day]: {
                                  start: e.target.value,
                                  end: weeklySchedule[day as keyof typeof weeklySchedule]!.end,
                                },
                              })
                            }
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                          />
                          <input
                            type="time"
                            value={weeklySchedule[day as keyof typeof weeklySchedule]!.end}
                            onChange={(e) =>
                              setWeeklySchedule({
                                ...weeklySchedule,
                                [day]: {
                                  start: weeklySchedule[day as keyof typeof weeklySchedule]!.start,
                                  end: e.target.value,
                                },
                              })
                            }
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  <Button
                    text={createWeeklyAllocationMutation.isPending ? "Creating..." : "Add Weekly Availability"}
                    onClick={handleCreateWeeklyAllocation}
                    disabled={
                      createWeeklyAllocationMutation.isPending ||
                      !weeklyStartDate ||
                      !weeklyEndDate ||
                      Object.keys(weeklySchedule).length === 0
                    }
                    appearance="primary"
                    className="w-full"
                  />

                  {weeklyFormError && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                      <Small className="text-red-800">{weeklyFormError}</Small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
